import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  LayoutDashboard, Users, Link2, LogOut, Wallet, Menu, X,
  FileText, Calendar, TrendingUp, Briefcase, LineChart, Coins, UserCog, Upload, Lock, Layers, AlertTriangle, Crown, Building2, RefreshCw, Percent, ShieldCheck, LifeBuoy, CalendarClock, Plug2, Radio, Clock, Wrench,
} from "lucide-react";
import { ForteShield } from "./BrandLogo";

const NAV_GROUPS = [
  {
    label: "Operação",
    roles: ["admin", "gestora", "indicador", "socio"],
    items: [
      { to: "/painel", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "indicador", "socio"] },
      { to: "/clientes", label: "Clientes", icon: Users, roles: ["admin", "gestora", "indicador", "socio"] },
      { to: "/clientes/bloqueados", label: "Bloqueados", icon: Lock, roles: ["admin", "gestora"] },
      { to: "/contratos", label: "Contratos", icon: FileText, roles: ["admin", "gestora", "socio"] },
      { to: "/parcelados", label: "Parcelados e Acordos", icon: Layers, roles: ["admin", "gestora", "socio"] },
      { to: "/vencimentos", label: "Vencimentos", icon: Calendar, roles: ["admin", "gestora", "socio"] },
      { to: "/cobranca", label: "Cobrança", icon: AlertTriangle, roles: ["admin", "gestora", "socio"] },
      { to: "/recuperacao", label: "Recuperação", icon: LifeBuoy, roles: ["admin", "gestora", "socio"] },
    ],
  },
  {
    label: "Gestão",
    roles: ["admin", "socio", "indicador"],
    items: [
      { to: "/financeiro", label: "Dashboard Financeiro", icon: TrendingUp, roles: ["admin", "socio"] },
      { to: "/dashboard/executivo", label: "Dashboard Executivo", icon: Crown, roles: ["admin"] },
      { to: "/fechamento", label: "Fechamento Mensal", icon: CalendarClock, roles: ["admin"] },
      { to: "/tesouraria", label: "Tesouraria", icon: Coins, roles: ["admin", "socio"] },
      { to: "/previsao", label: "Previsão Financeira", icon: LineChart, roles: ["admin", "socio"] },
      { to: "/comissoes", label: "Comissões", icon: Wallet, roles: ["admin", "indicador"] },
    ],
  },
  {
    label: "Cadastros",
    roles: ["admin"],
    items: [
      { to: "/socios", label: "Sócios", icon: Briefcase, roles: ["admin"] },
      { to: "/indicadores", label: "Indicadores", icon: Link2, roles: ["admin"] },
      { to: "/usuarios", label: "Usuários", icon: UserCog, roles: ["admin"] },
    ],
  },
  {
    label: "Administração",
    roles: ["admin"],
    items: [
      { to: "/admin/auditoria", label: "Auditoria Financeira", icon: ShieldCheck, roles: ["admin"] },
      { to: "/admin/central-ajustes", label: "Central de Ajustes", icon: Wrench, roles: ["admin"] },
      { to: "/integracoes", label: "Integrações", icon: Plug2, roles: ["admin"] },
      { to: "/comunicacao", label: "Centro de Comunicação", icon: Radio, roles: ["admin"] },
      { to: "/admin/recalcular-status", label: "Recalcular Status", icon: RefreshCw, roles: ["admin"] },
      { to: "/admin/juros-rapido", label: "Edição Rápida de Juros", icon: Percent, roles: ["admin"] },
      { to: "/importacao", label: "Importação", icon: Upload, roles: ["admin"] },
      { to: "/admin/capital-proprio", label: "Capital Próprio", icon: Building2, roles: ["admin"] },
    ],
  },
];

function NavLink({ item, isActive, onClick }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-[13.5px] transition-colors ${
        isActive
          ? "bg-[#1A1A1A] text-white border border-[#262626]"
          : "text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
      }`}
    >
      <Icon size={16} strokeWidth={1.8} className={isActive ? "text-[#D4AF37]" : ""} />
      {item.label}
    </Link>
  );
}

function SessionCard({ user, onLogout }) {
  return (
    <div className="px-3 py-3 rounded-md bg-[#141414] border border-[#1f1f1f]">
      <div className="text-[11px] uppercase tracking-wider text-[#737373]">Sessão</div>
      <div className="text-sm mt-0.5 truncate" data-testid="current-user-name">{user?.name}</div>
      <div className="text-[11px] text-[#A3A3A3] truncate">{user?.email}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1.5 text-[#D4AF37]">{user?.role}</div>
      <button
        onClick={onLogout}
        className="mt-3 w-full flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-md border border-[#262626] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
        data-testid="logout-button"
      >
        <LogOut size={14} /> Sair
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [systemTime, setSystemTime] = useState(null);

  // Busca timezone/hora do sistema (BRT) para exibir no rodapé
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/system/time");
        if (alive) setSystemTime(data);
      } catch (_) { /* silent */ }
    };
    load();
    const interval = setInterval(load, 60_000); // refresh a cada minuto
    return () => { alive = false; clearInterval(interval); };
  }, []);

  const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + "/");

  // Bloqueia scroll do body quando drawer aberto
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  const visibleGroups = NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => i.roles.includes(user?.role)) }))
    .filter(g => g.items.length > 0);
  const handleLogout = async () => { await logout(); nav("/login"); };

  const renderNav = (onItemClick) => (
    <>
      {visibleGroups.map(group => (
        <div key={group.label} className="mb-4">
          <div className="px-3 py-1.5 text-[9.5px] uppercase tracking-[0.2em] text-[#737373] font-medium">
            {group.label}
          </div>
          <div className="space-y-1">
            {group.items.map(item => (
              <NavLink key={item.to} item={item} isActive={isActive(item.to)} onClick={onItemClick}/>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-[#1f1f1f] bg-[#0d0d0d]">
        <div className="px-6 py-7 border-b border-[#1f1f1f]">
          <div className="flex items-center gap-3">
            <ForteShield size={34} />
            <div>
              <div className="font-display text-[15px] font-bold leading-none tracking-[0.04em]">FORTE CAPITAL</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mt-1.5">Painel de Clientes</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 overflow-y-auto">
          {renderNav()}
        </nav>
        <div className="px-3 pb-5 mt-auto">
          <SessionCard user={user} onLogout={handleLogout} />
        </div>
      </aside>

      {/* Topbar mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0d0d0d] border-b border-[#1f1f1f] flex items-center justify-between px-3 py-3">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 text-[#A3A3A3] hover:text-white"
          aria-label="Abrir menu"
          data-testid="mobile-menu-button"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <ForteShield size={22} />
          <span className="font-display font-bold text-[13px] tracking-[0.04em]">FORTE CAPITAL</span>
        </div>
        <div className="w-9" />
      </div>

      {/* Drawer mobile */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-[78%] max-w-[300px] bg-[#0d0d0d] border-r border-[#1f1f1f] flex flex-col overflow-y-auto"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="flex items-center gap-2.5">
                <ForteShield size={28} />
                <div>
                  <div className="font-display font-bold text-[13px] tracking-[0.04em]">FORTE CAPITAL</div>
                  <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#D4AF37] mt-0.5">Painel</div>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 text-[#A3A3A3] hover:text-white"
                aria-label="Fechar menu"
                data-testid="mobile-drawer-close"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 overflow-y-auto">
              {renderNav(() => setDrawerOpen(false))}
            </nav>
            <div className="px-3 pb-5 mt-auto border-t border-[#1f1f1f] pt-4">
              <SessionCard user={user} onLogout={async () => { setDrawerOpen(false); await handleLogout(); }} />
            </div>
          </aside>
        </>
      )}

      <main className="flex-1 md:ml-0 pt-14 md:pt-0 overflow-x-clip w-full">
        <div className="px-4 sm:px-6 lg:px-10 py-5 md:py-6 pb-10 max-w-[1600px] mx-auto">
          {children}
          {/* Rodapé discreto — timezone do sistema */}
          <div
            className="mt-8 pt-4 pb-16 md:pb-4 md:pr-40 border-t border-[#1a1a1a] flex items-center justify-end gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#525252]"
            data-testid="system-timezone-footer"
            title={systemTime?.now_local_iso || ""}
          >
            <Clock size={10} className="opacity-70"/>
            <span>Horário do sistema: <span className="text-[#737373]">Brasília</span></span>
          </div>
        </div>
      </main>
    </div>
  );
}
